import { appRpc } from "./rpc";
import { createResource } from "./resource";
import * as map from "./mapping";
import type * as ui from "./types";

export const upsert = <T extends { id: string }>(rows: T[], row: T) => {
  const index = rows.findIndex((r) => r.id === row.id);
  if (index < 0) return [...rows, row];
  const next = [...rows];
  next[index] = row;
  return next;
};
interface AccountsState {
  accounts: ui.Account[];
  balances: Record<string, ui.Balance[]>;
  positions: Record<string, ui.Position[]>;
  leverages: Record<string, ui.Leverage[]>;
  orders: Record<string, ui.OrderData[]>;
}
const emptyAccounts = (): AccountsState => ({
  accounts: [],
  balances: {},
  positions: {},
  leverages: {},
  orders: {},
});
export const accounts = createResource(
  emptyAccounts,
  (signal) => appRpc.subscribeAccounts({}, { signal }),
  (state, event) => {
    const p = event.payload;
    switch (p.case) {
      case "snapshot":
        return { ...emptyAccounts(), accounts: p.value.accounts.map(map.account) };
      case "upserted":
        return { ...state, accounts: upsert(state.accounts, map.account(p.value)) };
      case "deleted": {
        const id = p.value.value;
        const next = { ...state, accounts: state.accounts.filter((r) => r.id !== id) };
        const omit = <T>(values: Record<string, T>) => {
          const copy = { ...values };
          delete copy[id];
          return copy;
        };
        next.balances = omit(state.balances);
        next.positions = omit(state.positions);
        next.leverages = omit(state.leverages);
        next.orders = omit(state.orders);
        return next;
      }
      case "balance":
        return {
          ...state,
          balances: {
            ...state.balances,
            [p.value.accountId!.value]: p.value.balances.map(map.plain),
          },
        };
      case "position":
        return {
          ...state,
          positions: {
            ...state.positions,
            [p.value.accountId!.value]: p.value.positions.map(map.position),
          },
        };
      case "leverage":
        return {
          ...state,
          leverages: {
            ...state.leverages,
            [p.value.accountId!.value]: p.value.leverages.map(map.plain),
          },
        };
      case "order": {
        const row: ui.OrderData = {
          ...map.plain(p.value),
          side: p.value.side === 1 ? "BUY" : "SELL",
          type: p.value.type === 1 ? "MARKET" : "LIMIT",
        };
        return {
          ...state,
          orders: {
            ...state.orders,
            [row.account_id]: [
              row,
              ...(state.orders[row.account_id] || []).filter((r) => r.order_id !== row.order_id),
            ].slice(0, 100),
          },
        };
      }
      default:
        return state;
    }
  }
);
export const scripts = createResource<
  ui.Script[],
  Awaited<ReturnType<typeof appRpc.subscribeScripts>> extends AsyncIterable<infer E> ? E : never
>(
  () => [],
  (signal) => appRpc.subscribeScripts({}, { signal }),
  (rows, event) => {
    const p = event.payload;
    if (p.case === "snapshot") return p.value.scripts.map(map.plain);
    if (p.case === "upserted") return upsert(rows, map.plain(p.value) as ui.Script);
    if (p.case === "deleted") return rows.filter((r) => r.id !== p.value.value);
    return rows;
  }
);
export const backtests = createResource(
  () => [] as ui.Backtest[],
  (signal) => appRpc.subscribeBacktests({}, { signal }),
  (rows, event) => {
    const p = event.payload;
    if (p.case === "snapshot") return p.value.backtests.map(map.backtest);
    if (p.case === "upserted") return upsert(rows, map.backtest(p.value));
    if (p.case === "deleted") return rows.filter((r) => r.id !== p.value.value);
    if (p.case === "result")
      return rows.map((r) =>
        r.id === p.value.backtestId?.value
          ? { ...r, result: p.value.result ? map.result(p.value.result) : null }
          : r
      );
    return rows;
  }
);
export const resources = { accounts, scripts, backtests };
export type ResourceName = keyof typeof resources;
export interface OrderScope {
  account_id?: string;
  backtest_id?: string;
  status?: string;
  limit?: number;
}
export function reduceOrders(rows: ui.OrderRecord[], row: ui.OrderRecord, scope: OrderScope) {
  const kept = rows.filter((r) => r.id !== row.id);
  if (
    (!scope.account_id || row.account_id === scope.account_id) &&
    (!scope.backtest_id || row.backtest_id === scope.backtest_id) &&
    (!scope.status || row.status === scope.status)
  )
    kept.push(row);
  return kept
    .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id))
    .slice(0, scope.limit || 200);
}
const orderResources = new Map<string, ReturnType<typeof newOrderResource>>();
function newOrderResource(scope: OrderScope, onExpire: () => void) {
  return createResource(
    () => [] as ui.OrderRecord[],
    (signal) =>
      appRpc.subscribeOrders(
        {
          accountId: scope.account_id ? { value: scope.account_id } : undefined,
          backtestId: scope.backtest_id ? { value: scope.backtest_id } : undefined,
          status: scope.status || undefined,
          limit: scope.limit || 200,
        },
        { signal }
      ),
    (rows, event) => {
      const p = event.payload;
      if (p.case === "snapshot") return p.value.orders.map(map.order);
      if (p.case === "upserted") return reduceOrders(rows, map.order(p.value), scope);
      return rows;
    },
    { idleMs: 5 * 60 * 1000, onExpire }
  );
}
export function ordersFor(scope: OrderScope) {
  const key = JSON.stringify([
    scope.account_id || "",
    scope.backtest_id || "",
    scope.status || "",
    scope.limit || 200,
  ]);
  let resource = orderResources.get(key);
  if (!resource) {
    resource = newOrderResource({ ...scope }, () => {
      orderResources.delete(key);
    });
    orderResources.set(key, resource);
  }
  return resource;
}
